import type { Lens } from '@hookform/lenses';
import { useWatch } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { getReadonlyListItemString, getReadonlyListItemYesOrNo } from '@interxfi/app-components/components/readonly-list/util';
import type { IxcRegistrationV1ControlPerson } from '@interxfi/types/generated/api';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import type { FormConfirmationGroup } from '../../../../../../components/form/confirmation/form-confirmation';
import { getRegistrationControlPersonListItems } from '../../../profile-list-items';
import {
  DirectBeneficialOwnerControlPersonControlPersonForm,
  type DirectBeneficialOwnerControlPersonFormValues,
  directBeneficialOwnerControlPersonFormSchema,
  formValuesToDirectBeneficialOwnerControlPerson,
  getInitialFormValuesForDirectBeneficialOwnerControlPerson,
} from './beneficial-owner/direct-beneficial-owner-control-person-form';
import {
  formValuesToIndirectBeneficialOwnerControlPerson,
  getInitialFormValuesForIndirectBeneficialOwner,
  IndirectBeneficialOwnerControlPersonForm,
  type IndirectBeneficialOwnerControlPersonFormValues,
  indirectBeneficialOwnerControlPersonFormSchema,
} from './beneficial-owner/indirect-beneficial-owner-control-person-form';
import {
  DirectorControlPersonForm,
  type DirectorControlPersonFormValues,
  directorControlPersonFormSchema,
  formValuesToDirectorControlPerson,
  getInitialFormValuesForDirectorControlPerson,
} from './director-control-person-form';
import {
  formValuesToOfficerControlPerson,
  getInitialFormValuesForOfficerControlPerson,
  OfficerControlPersonForm,
  type OfficerControlPersonFormValues,
  officerControlPersonFormSchema,
} from './officer-control-person-form';
import {
  formValuesToOtherControlPerson,
  getInitialFormValuesForOtherControlPerson,
  OtherControlPersonForm,
  type OtherControlPersonFormValues,
  otherControlPersonFormSchema,
} from './other-control-person-form';

export const controlPersonFormSchema = z.discriminatedUnion('kind', [
  directBeneficialOwnerControlPersonFormSchema,
  indirectBeneficialOwnerControlPersonFormSchema,
  directorControlPersonFormSchema,
  officerControlPersonFormSchema,
  otherControlPersonFormSchema,
]);

export type ControlPersonFormValues = z.infer<typeof controlPersonFormSchema>;

export function getInitialFormValuesForControlPerson(
  data: IxcRegistrationV1ControlPerson,
  primaryContactProfileId: string | undefined,
): ControlPersonFormValues {
  return match(data)
    .with({ beneficialOwner: { direct: P.not(P.nullish) } }, (r) =>
      getInitialFormValuesForDirectBeneficialOwnerControlPerson(r.beneficialOwner.direct, primaryContactProfileId),
    )
    .with({ beneficialOwner: { indirect: P.not(P.nullish) } }, (r) =>
      getInitialFormValuesForIndirectBeneficialOwner(r.beneficialOwner.indirect, primaryContactProfileId),
    )
    .with({ director: P.not(P.nullish) }, (r) => getInitialFormValuesForDirectorControlPerson(r.director, primaryContactProfileId))
    .with({ officer: P.not(P.nullish) }, (r) => getInitialFormValuesForOfficerControlPerson(r.officer, primaryContactProfileId))
    .with({ other: P.not(P.nullish) }, (r) => getInitialFormValuesForOtherControlPerson(r.other, primaryContactProfileId))
    .exhaustive();
}

export function formValuesToControlPerson(values: ControlPersonFormValues): IxcRegistrationV1ControlPerson {
  return match(values)
    .with({ kind: 'directBeneficialOwner' }, (r) => ({
      beneficialOwner: { direct: formValuesToDirectBeneficialOwnerControlPerson(r) },
    }))
    .with({ kind: 'indirectBeneficialOwner' }, (r) => ({
      beneficialOwner: { indirect: formValuesToIndirectBeneficialOwnerControlPerson(r) },
    }))
    .with({ kind: 'director' }, (r) => ({
      director: formValuesToDirectorControlPerson(r),
    }))
    .with({ kind: 'officer' }, (r) => ({
      officer: formValuesToOfficerControlPerson(r),
    }))
    .with({ kind: 'other' }, (r) => ({
      other: formValuesToOtherControlPerson(r),
    }))
    .exhaustive();
}

export function formValuesToPrimaryContactProfileId(values: ControlPersonFormValues[]): string | undefined {
  for (const value of values) {
    const primaryContact = match(value)
      .with({ kind: 'directBeneficialOwner' }, (r) => (r.primaryContact ? r.value.profileId : undefined))
      .with({ kind: 'indirectBeneficialOwner' }, (r) => (r.primaryContact ? r.value.profileId : undefined))
      .with({ kind: 'director' }, (r) => (r.primaryContact ? r.value.profileId : undefined))
      .with({ kind: 'officer' }, (r) => (r.primaryContact ? r.value.profileId : undefined))
      .with({ kind: 'other' }, (r) => (r.primaryContact ? r.value.profileId : undefined))
      .exhaustive();

    if (primaryContact) {
      return primaryContact;
    }
  }

  return undefined;
}

export function mapControlPersonFormValuesToConfirmationDetails(values: ControlPersonFormValues): FormConfirmationGroup[] {
  const items = getRegistrationControlPersonListItems(formValuesToControlPerson(values));
  const primaryContactListItem = getReadonlyListItemYesOrNo('primaryContact', 'Primary Contact', values.primaryContact);

  return [
    {
      id: 'main',
      list: {
        items: [
          ...match(items)
            .with({ beneficialOwner: { value: { direct: P.not(P.nullish) } } }, (r) => [
              getReadonlyListItemString('beneficialOwnerType', 'Type', 'Direct Beneficial Owner'),
              r.beneficialOwner.value.direct.value.profile,
              r.beneficialOwner.value.direct.value.ownership,
              r.beneficialOwner.value.direct.value.signer,
              primaryContactListItem,
            ])
            .with({ beneficialOwner: { value: { indirect: P.not(P.nullish) } } }, (r) => [
              getReadonlyListItemString('beneficialOwnerType', 'Type', 'Indirect Beneficial Owner'),
              r.beneficialOwner.value.indirect.value.profile,
              r.beneficialOwner.value.indirect.value.ownership,
              r.beneficialOwner.value.indirect.value.signer,
              primaryContactListItem,
              r.beneficialOwner.value.indirect.value.beneficialOwnerEntity,
              r.beneficialOwner.value.indirect.value.passThroughEntities,
            ])
            .with({ director: P.not(P.nullish) }, (r) => [
              items.typeName,
              r.director.value.profile,
              r.director.value.signer,
              primaryContactListItem,
              r.director.value.title,
            ])
            .with({ officer: P.not(P.nullish) }, (r) => [
              items.typeName,
              r.officer.value.profile,
              r.officer.value.signer,
              primaryContactListItem,
              r.officer.value.title,
            ])
            .with({ other: P.not(P.nullish) }, (r) => [
              items.typeName,
              r.other.value.profile,
              r.other.value.signer,
              primaryContactListItem,
              r.other.value.title,
            ])
            .otherwise(() => []),
        ],
      },
    },
  ];
}

interface ControlPersonFormProps {
  lens: Lens<ControlPersonFormValues>;
  programId: string | undefined;
}

export function ControlPersonForm({ lens, programId }: ControlPersonFormProps) {
  const kind = useWatch(lens.focus('kind').interop());

  switch (kind) {
    case 'directBeneficialOwner':
      return (
        <DirectBeneficialOwnerControlPersonControlPersonForm
          lens={lens.narrow<DirectBeneficialOwnerControlPersonFormValues>()}
          programId={programId}
        />
      );
    case 'indirectBeneficialOwner':
      return <IndirectBeneficialOwnerControlPersonForm lens={lens.narrow<IndirectBeneficialOwnerControlPersonFormValues>()} programId={programId} />;
    case 'director':
      return <DirectorControlPersonForm lens={lens.narrow<DirectorControlPersonFormValues>()} programId={programId} />;
    case 'officer':
      return <OfficerControlPersonForm lens={lens.narrow<OfficerControlPersonFormValues>()} programId={programId} />;
    case 'other':
      return <OtherControlPersonForm lens={lens.narrow<OtherControlPersonFormValues>()} programId={programId} />;
    default:
      exhaustiveCheck(kind);

      return null;
  }
}
